import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveCampaign,
  executeCampaign,
  getCampaign,
  issueUploadUrl,
  updateCampaign,
  type OutboundCampaign,
} from '../api/campaigns.js';
import { listConnectors } from '../api/connectors.js';
import { listTemplates } from '../api/templates.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const STATUS_TONE = {
  draft: 'slate',
  scheduled: 'yellow',
  running: 'yellow',
  completed: 'green',
  archived: 'slate',
} as const;

const MAX_CLIENT_COUNT_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Cuenta filas de un CSV (excluyendo cabecera y líneas vacías).
 * Lee todo el texto vía FileReader — sólo apto para archivos chicos.
 */
async function estimateRows(file: File): Promise<number> {
  if (file.size > MAX_CLIENT_COUNT_BYTES) return 0;
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return 0;
  // Excluir cabecera.
  return Math.max(0, lines.length - 1);
}

function formatCountdown(target: Date, now: Date): string {
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return t.campaign.countdownPast;
  const sec = Math.floor(diff / 1000);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins || (!days && !hours)) parts.push(`${mins}m`);
  return `${t.campaign.countdownIn} ${parts.join(' ')}`;
}

export function CampaignDetail(): JSX.Element {
  const { id = '', campaignId = '' } = useParams<{
    id: string;
    campaignId: string;
  }>();
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [executeAt, setExecuteAt] = useState<string>('');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  useEffect(() => {
    const handle = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(handle);
  }, []);

  const campaignQuery = useQuery({
    queryKey: ['campaign', id, campaignId],
    queryFn: () => getCampaign(id, campaignId),
    enabled: Boolean(id && campaignId),
  });

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id, { includeArchived: false }],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates', id, { includeArchived: false }],
    queryFn: () => listTemplates(id),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveCampaign(id, campaignId),
    onSuccess: () => {
      pushToast('success', t.campaign.archived);
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id, campaignId] });
      setConfirmArchive(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<OutboundCampaign> => {
      const current = campaignQuery.data;
      if (!current) throw new Error('campaign_not_loaded');
      const { uploadUrl, objectKey, requiredHeaders } = await issueUploadUrl(
        id,
        campaignId,
        file.name,
      );
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: requiredHeaders,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload PUT failed: ${putRes.status}`);
      }
      const contactsCount = await estimateRows(file);
      return updateCampaign(
        id,
        campaignId,
        { csvObjectKey: objectKey, contactsCount },
        current.version,
      );
    },
    onSuccess: (updated) => {
      pushToast(
        'success',
        `${t.campaign.csvUploaded} (${updated.contactsCount} ${t.campaign.fields.contactsCount.toLowerCase()})`,
      );
      queryClient.invalidateQueries({ queryKey: ['campaign', id, campaignId] });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const executeMutation = useMutation({
    mutationFn: (scheduledAt?: string) =>
      executeCampaign(id, campaignId, scheduledAt),
    onSuccess: () => {
      pushToast('success', t.campaign.executed);
      queryClient.invalidateQueries({ queryKey: ['campaign', id, campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
      setExecuteAt('');
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const campaign = campaignQuery.data;
  const connector = useMemo(
    () =>
      campaign
        ? (connectorsQuery.data ?? []).find((c) => c.id === campaign.connectorId)
        : undefined,
    [campaign, connectorsQuery.data],
  );
  const template = useMemo(
    () =>
      campaign
        ? (templatesQuery.data ?? []).find((tpl) => tpl.id === campaign.templateId)
        : undefined,
    [campaign, templatesQuery.data],
  );

  if (campaignQuery.isLoading) return <p>{t.app.loading}</p>;
  if (campaignQuery.error) {
    return (
      <p className="text-red-700">
        {(campaignQuery.error as Error).message}
      </p>
    );
  }
  if (!campaign) return <p>{t.campaign.notFound}</p>;

  const canExecute =
    Boolean(campaign.csvObjectKey) &&
    (campaign.status === 'draft' || campaign.status === 'scheduled');

  const scheduledDate = campaign.scheduledAt ? new Date(campaign.scheduledAt) : null;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {campaign.name}{' '}
            <StatusBadge
              label={t.campaign.status[campaign.status]}
              tone={STATUS_TONE[campaign.status]}
            />
          </h2>
          <p className="text-xs text-slate-500">
            v{campaign.version} ·{' '}
            {t.campaign.fields.createdAt}:{' '}
            {new Date(campaign.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/tenants/${id}/campaigns/${campaign.id}/edit`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50"
          >
            {t.campaign.edit}
          </Link>
          {!campaign.deletedAt && (
            <button
              type="button"
              onClick={() => setConfirmArchive(true)}
              className="rounded border border-red-300 px-3 py-1 text-sm text-red-700 hover:bg-red-50"
            >
              {t.campaign.archive}
            </button>
          )}
        </div>
      </div>

      {/* Datos básicos */}
      <section className="mb-4 rounded border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t.campaign.sections.basic}
        </h3>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-slate-500">{t.campaign.fields.connector}</dt>
          <dd>{connector?.name ?? campaign.connectorId}</dd>
          <dt className="text-slate-500">{t.campaign.fields.template}</dt>
          <dd>{template?.name ?? campaign.templateId}</dd>
          <dt className="text-slate-500">{t.campaign.fields.description}</dt>
          <dd>{campaign.description ?? '—'}</dd>
          {campaign.rateLimitPerSecond !== undefined && (
            <>
              <dt className="text-slate-500">
                {t.campaign.fields.rateLimitPerSecond}
              </dt>
              <dd>{campaign.rateLimitPerSecond}</dd>
            </>
          )}
        </dl>
      </section>

      {/* Programación */}
      <section className="mb-4 rounded border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t.campaign.sections.schedule}
        </h3>
        {scheduledDate ? (
          <p className="text-sm">
            {scheduledDate.toLocaleString()}{' '}
            <span
              data-testid="countdown"
              className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
            >
              {formatCountdown(scheduledDate, now)}
            </span>
          </p>
        ) : (
          <p className="text-sm text-slate-500">—</p>
        )}
      </section>

      {/* Métricas */}
      <section className="mb-4 rounded border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t.campaign.sections.metrics}
        </h3>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase text-slate-500">
              {t.campaign.fields.contactsCount}
            </div>
            <div data-testid="contacts-count" className="text-xl font-semibold">
              {campaign.contactsCount}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">
              {t.campaign.fields.deliveredCount}
            </div>
            <div className="text-xl font-semibold text-green-700">
              {campaign.deliveredCount}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">
              {t.campaign.fields.failedCount}
            </div>
            <div className="text-xl font-semibold text-red-700">
              {campaign.failedCount}
            </div>
          </div>
        </div>
      </section>

      {/* CSV */}
      <section className="mb-4 rounded border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t.campaign.sections.csv}
        </h3>
        {campaign.csvObjectKey ? (
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-mono text-xs text-slate-600">
                {campaign.csvObjectKey}
              </span>{' '}
              <span className="text-slate-500">
                ({campaign.contactsCount}{' '}
                {t.campaign.fields.contactsCount.toLowerCase()})
              </span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="text/csv"
              aria-label={t.campaign.pickCsv}
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              className="block text-sm"
            />
            {selectedFile && selectedFile.size > MAX_CLIENT_COUNT_BYTES && (
              <p className="text-xs text-yellow-700">
                {t.campaign.csvLargeWarning}
              </p>
            )}
            <button
              type="button"
              disabled={!selectedFile || uploadMutation.isPending}
              onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {uploadMutation.isPending
                ? t.campaign.csvUploading
                : t.campaign.replaceCsv}
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-slate-500">{t.campaign.csvNotPresent}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="text/csv"
              aria-label={t.campaign.pickCsv}
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              className="block text-sm"
            />
            {selectedFile && selectedFile.size > MAX_CLIENT_COUNT_BYTES && (
              <p className="text-xs text-yellow-700">
                {t.campaign.csvLargeWarning}
              </p>
            )}
            <button
              type="button"
              disabled={!selectedFile || uploadMutation.isPending}
              onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {uploadMutation.isPending
                ? t.campaign.csvUploading
                : t.campaign.uploadCsv}
            </button>
          </div>
        )}
      </section>

      {/* Ejecutar */}
      {canExecute && (
        <section
          data-testid="execute-section"
          className="mb-4 rounded border border-slate-200 bg-white p-4"
        >
          <h3 className="mb-2 text-sm font-semibold text-slate-700">
            {t.campaign.sections.execute}
          </h3>
          <div className="flex flex-wrap items-end gap-2 text-sm">
            <button
              type="button"
              disabled={executeMutation.isPending}
              onClick={() => executeMutation.mutate(undefined)}
              className="rounded bg-brand-500 px-3 py-1 text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {t.campaign.executeNow}
            </button>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-slate-600">
                {t.campaign.fields.scheduledAt}
              </span>
              <input
                type="datetime-local"
                value={executeAt}
                onChange={(e) => setExecuteAt(e.target.value)}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <button
              type="button"
              disabled={!executeAt || executeMutation.isPending}
              onClick={() => {
                const iso = new Date(executeAt).toISOString();
                executeMutation.mutate(iso);
              }}
              className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              {t.campaign.schedule}
            </button>
          </div>
        </section>
      )}

      <ConfirmModal
        open={confirmArchive}
        title={t.campaign.confirmArchiveTitle}
        description={t.campaign.confirmArchiveDesc}
        confirmLabel={t.campaign.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => archiveMutation.mutate()}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
}
